import { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonIcon, Skeleton, Tab, TabList, Tabs } from '@backstage/ui';
import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';

import styles from './Navigation.module.css';

interface NavigationData {
  type: string;
  label: string;
  count?: number;
}

export interface NavigationProps {
  loading: boolean;
  selectedItemType: string;
  handleNavigation: (itemType: string) => void;
  navigationData?: NavigationData[];
  /**
   * Tabs of equal width that fill the row, a few per view on narrow screens,
   * with arrow buttons to scroll through the rest. Without it the tabs keep
   * their natural width and scroll when they do not fit.
   * @defaultValue true
   */
  isSliderEnabled?: boolean;
  className?: string;
  /**
   * Accessible name of the tab list.
   * @defaultValue 'Navigation'
   */
  'aria-label'?: string;
}

/** Share of the visible width that one arrow press scrolls */
const SCROLL_STEP = 0.8;

/**
 * Horizontal tabs with an optional count per tab, on the Backstage UI
 * `Tabs`. The selection is controlled: `handleNavigation` is called with the
 * `type` of the chosen tab.
 *
 * @example
 * <Navigation
 *   loading={loading}
 *   navigationData={[{ type: 'system', label: 'System', count: 12 }]}
 *   selectedItemType={selectedKind}
 *   handleNavigation={setSelectedKind}
 * />
 */
export const Navigation = ({
  loading,
  selectedItemType,
  handleNavigation,
  navigationData,
  isSliderEnabled = true,
  className,
  'aria-label': ariaLabel = 'Navigation',
}: NavigationProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const updateArrows = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    setCanScrollBack(scrollLeft > 0);
    setCanScrollForward(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!isSliderEnabled || !scroller) {
      return undefined;
    }
    updateArrows();
    // The tabs render after this effect (React Aria builds the collection
    // first), so watch the content as well as the size of the scroller
    const mutationObserver = new MutationObserver(updateArrows);
    mutationObserver.observe(scroller, { childList: true, subtree: true });
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(updateArrows);
    resizeObserver?.observe(scroller);
    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, [isSliderEnabled, loading, navigationData, updateArrows]);

  const scroll = (direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    if (scroller) {
      scroller.scrollLeft += direction * scroller.clientWidth * SCROLL_STEP;
    }
  };

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      data-slider={isSliderEnabled || undefined}
      data-testid="navigation-container"
    >
      {loading ? (
        <Skeleton
          className={styles.loading}
          width="100%"
          height="1.5rem"
          aria-label="Loading"
          data-testid="navigation-loading"
        />
      ) : (
        <>
          {isSliderEnabled && canScrollBack && (
            <ButtonIcon
              variant="secondary"
              size="small"
              icon={<RiArrowLeftSLine />}
              aria-label="Scroll tabs back"
              className={styles.arrow}
              onPress={() => scroll(-1)}
            />
          )}
          <div
            ref={scrollerRef}
            className={styles.scroller}
            data-testid="navigation-items-container"
            onScroll={isSliderEnabled ? updateArrows : undefined}
          >
            <Tabs
              selectedKey={selectedItemType}
              onSelectionChange={key => {
                if (key !== selectedItemType) {
                  handleNavigation(String(key));
                }
              }}
              className={styles.tabs}
            >
              <TabList aria-label={ariaLabel} className={styles.tabList}>
                {navigationData?.map(item => (
                  <Tab key={item.type} id={item.type} className={styles.tab}>
                    {item.label}
                    {/* The space keeps label and count apart in the accessible name */}
                    {item.count !== undefined && ' '}
                    {item.count !== undefined && (
                      <span className={styles.count}>{item.count}</span>
                    )}
                  </Tab>
                ))}
              </TabList>
            </Tabs>
          </div>
          {isSliderEnabled && canScrollForward && (
            <ButtonIcon
              variant="secondary"
              size="small"
              icon={<RiArrowRightSLine />}
              aria-label="Scroll tabs forward"
              className={styles.arrow}
              onPress={() => scroll(1)}
            />
          )}
        </>
      )}
    </div>
  );
};
